import React from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { CustodianItem } from '@/types';
import { clsx } from 'clsx';
import { CheckCircleIcon, StopCircleIcon, PencilSquareIcon } from '@heroicons/react/24/outline';

// LR-441: columns for the Custodians register tab. Mirrors counterparty-columns
// but scoped to the four CRUD fields (address is admin-only / out of scope).
export const createCustodianColumns = (
  onEdit?: (custodian: CustodianItem) => void,
): ColumnDef<CustodianItem>[] => [
  {
    accessorKey: 'name',
    header: 'Custodian Name',
    size: 280,
    cell: (info) => (
      <span className="font-medium text-gray-900 truncate">{info.getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'short_code',
    header: 'Short Code',
    size: 160,
    cell: (info) => (
      <span className="text-gray-500 font-mono text-xs">{info.getValue() as string}</span>
    ),
  },
  {
    accessorKey: 'account_number',
    header: 'Account Number',
    size: 200,
    cell: (info) => {
      const value = info.getValue() as string;
      return value ? (
        <span className="text-xs font-mono tabular-nums text-gray-700">{value}</span>
      ) : (
        <span className="text-gray-400">—</span>
      );
    },
  },
  {
    accessorKey: 'is_active',
    header: 'Status',
    size: 120,
    cell: (info) => {
      const isActive = info.getValue() as boolean;
      return (
        <span
          className={clsx(
            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
            isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800',
          )}
        >
          {isActive ? (
            <CheckCircleIcon className="h-3 w-3 mr-1" />
          ) : (
            <StopCircleIcon className="h-3 w-3 mr-1" />
          )}
          {isActive ? 'Active' : 'Inactive'}
        </span>
      );
    },
  },
  // LR-584: the Edit column only renders when the caller passes onEdit — the
  // page omits it for users without the manage_registries capability.
  ...(onEdit ? [{
    id: 'edit',
    header: 'Edit',
    size: 120,
    cell: (info) => (
      <button
        type="button"
        aria-label={`Edit ${info.row.original.name}`}
        onClick={(e) => {
          e.stopPropagation();
          onEdit(info.row.original);
        }}
        className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors border border-blue-200"
      >
        <PencilSquareIcon className="h-3 w-3 mr-1" />
        Edit
      </button>
    ),
  } satisfies ColumnDef<CustodianItem>] : []),
];
